import { useThrottle } from '@insify/react-hooks';
import { captureMessage } from '@sentry/react';
import * as React from 'react';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { usePageTransition } from '../../PageTransition/PageTransitionProvider';

import type { WizardStep } from '..';

type State = {
  slug?: WizardStep;
  nextStep?: WizardStep;
  previousStep?: WizardStep;
  showNextPage: () => void;
  showPreviousPage: () => void;
  showSpecificPage: (slug: string) => void;
  navigate: ReturnType<typeof useNavigate>;
};

export type UseWizardProviderProps = {
  steps: WizardStep[];
  children: React.ReactNode;
  basePath: string;
  onLeave?: () => void;
  onFinish?: () => void;
  currentIndexPredicate: (steps: WizardStep[], pathname: string) => number;
};

const wizardContext = React.createContext<State | undefined>(undefined);

/** This version is for frontends that have their own react router BrowserRouter component */
const wizardWithoutRouterContext = React.createContext<State | undefined>(undefined);

export type UseWizardWithoutRouterProviderProps = Omit<UseWizardProviderProps, 'basePath'>;
function UseWizardWithoutRouterProvider({
  children,
  steps,
  onFinish,
  onLeave,
  currentIndexPredicate,
}: UseWizardWithoutRouterProviderProps): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setDirection } = usePageTransition();

  const nextStep = useMemo((): WizardStep | undefined => {
    const currentIndex = currentIndexPredicate(steps, pathname);
    const nextIndex = steps.length - 1 === currentIndex ? undefined : currentIndex + 1;
    return nextIndex ? steps.find((step) => step.slug === steps[nextIndex]?.slug) : undefined;
  }, [pathname, steps.map(({ slug }) => slug).join(',')]);

  const previousStep = useMemo((): WizardStep | undefined => {
    const currentIndex = currentIndexPredicate(steps, pathname);
    const previousIndex = currentIndex === 0 ? undefined : currentIndex - 1;
    return previousIndex !== undefined ? steps.find((step) => step.slug === steps[previousIndex]?.slug) : undefined;
  }, [pathname, steps.map(({ slug }) => slug).join(',')]);

  function showNextPage(): void {
    if (nextStep === undefined) {
      captureMessage('Tried to navigate to the next page but there is no next page', {
        level: 'error',
        extra: {
          note: 'This is probably because the data was not refetched after the last command, so the next step is not available yet',
          pathname,
          nextStep,
          steps,
        },
      });
    }
    setDirection('forward');
    nextStep ? navigate(nextStep.slug) : onFinish?.();
  }

  const throttledShowNextPage = useThrottle(showNextPage, 500);

  function showPreviousPage(): void {
    setDirection('backward');
    previousStep ? navigate(previousStep.slug) : onLeave?.();
  }

  function showSpecificPage(slug: string): void {
    const index = steps.findIndex((step) => step.slug === slug);
    if (index === -1) {
      throw new Error(`Step with slug ${slug} not found`);
    }

    const currentIndex = currentIndexPredicate(steps, pathname);
    if (index > currentIndex) {
      setDirection('forward');
    } else {
      setDirection('backward');
    }
    navigate(slug);
  }

  return (
    <wizardWithoutRouterContext.Provider
      value={{
        nextStep,
        previousStep,
        showNextPage: throttledShowNextPage, // Using a throttled version to prevent skipping pages when tapping the continue button multiple times in quick succession
        showPreviousPage,
        showSpecificPage,
        navigate,
      }}
    >
      {children}
    </wizardWithoutRouterContext.Provider>
  );
}

function useWizard(): State {
  const contextWithRouter = React.useContext(wizardContext);
  const contextWithoutRouter = React.useContext(wizardWithoutRouterContext);
  const context = contextWithRouter || contextWithoutRouter;

  if (context === undefined) {
    throw new Error('useWizard must be used within a <Wizard> component');
  }
  return context;
}

export { UseWizardWithoutRouterProvider, useWizard };
